import moment, { Moment } from 'moment';
import { DEFAULT_DATE_FORMAT } from 'api/models';
import {
  TermSeparator,
  ValueSeparator,
} from 'components/organisms/tasks/filters/separators';
import { unreachable } from 'utils/unreachable';
import { DateRange, getRangeObject, Range } from './types';

export const stringifyDateRange = (range: DateRange): string => {
  switch (range.type) {
    case Range.ThisWeek:
    case Range.LastWeek:
    case Range.ThisMonth:
    case Range.LastMonth:
    case Range.ThisQuarter:
    case Range.LastQuarter:
    case Range.Last30Days:
    case Range.Last60Days:
    case Range.Last90Days:
    case Range.Last180Days:
    case Range.Last12Months:
    case Range.Last10Years:
    case Range.ThisYear:
    case Range.Anytime:
      return range.type;
    case Range.Custom:
    case Range.Advanced:
      return `${range.type}${TermSeparator}${range.startDate.format(
        DEFAULT_DATE_FORMAT,
      )}${ValueSeparator}${range.endDate.format(DEFAULT_DATE_FORMAT)}`;
    default:
      return unreachable(range);
  }
};

interface InvalidDateRangeFromQuery {
  valid: false;
}

interface ValidDateRangeFromQuery {
  valid: true;
  startDate: Moment;
  endDate: Moment;
}

type DateRangeFromQuery = InvalidDateRangeFromQuery | ValidDateRangeFromQuery;

const parseRangeValues = (value: string) =>
  value.split(ValueSeparator).map((x) => moment(x, DEFAULT_DATE_FORMAT));

const getRangeValue = (value: string): DateRangeFromQuery => {
  const valueAsMoment = parseRangeValues(value ?? '');

  const isValid =
    valueAsMoment.length === 2 &&
    valueAsMoment[0].isValid() &&
    valueAsMoment[1].isValid() &&
    valueAsMoment[0].isSameOrBefore(valueAsMoment[1], 'day');

  return isValid
    ? { valid: true, startDate: valueAsMoment[0], endDate: valueAsMoment[1] }
    : { valid: false };
};

export const parseDateRange = (
  queryString: string,
  defaultRange: DateRange,
): DateRange => {
  const [rangeType, value] = queryString.split(TermSeparator);

  const rangeValue = getRangeValue(value);

  switch (rangeType) {
    case Range.ThisWeek:
    case Range.LastWeek:
    case Range.ThisMonth:
    case Range.LastMonth:
    case Range.ThisQuarter:
    case Range.LastQuarter:
    case Range.Last30Days:
    case Range.Last60Days:
    case Range.Last90Days:
    case Range.Last180Days:
    case Range.Last12Months:
    case Range.ThisYear:
    case Range.Anytime:
      return getRangeObject(rangeType);
    case Range.Custom:
    case Range.Advanced:
      return rangeValue.valid
        ? getRangeObject(rangeType, rangeValue.startDate, rangeValue.endDate)
        : defaultRange;
    default:
      return defaultRange;
  }
};
